/*
 * product.js — product page behaviour (vanilla).
 *  - gallery: 2-column image grid on desktop; scroll-snap track with dots and counter on phones
 *  - strap pills: switch variant (id, price, URL)
 *  - add-on toggles: running total in the button label
 *  - one /cart/add.js call for the watch + toggled add-ons (window.Cart from theme.js)
 *  - sticky bar: appears once the main button has scrolled out of view
 */
(function () {
  'use strict';

  function money(cents) {
    if (window.Cart && window.Cart.formatMoney) return window.Cart.formatMoney(cents);
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* ------------------------------------------------------------------ */
  /* Gallery                                                             */
  /* ------------------------------------------------------------------ */
  function initGallery(gallery) {
    var track = gallery.querySelector('[data-gallery-track]');
    if (!track) return;
    var slides = gallery.querySelectorAll('[data-gallery-slide]');
    var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    var dots = gallery.querySelectorAll('[data-gallery-dot]');
    var counter = gallery.querySelector('[data-gallery-index]');
    var prev = gallery.querySelector('[data-gallery-prev]');
    var next = gallery.querySelector('[data-gallery-next]');
    var index = 0;
    if (slides.length < 2) return;

    function paint(i) {
      index = i;
      thumbs.forEach(function (t, n) {
        t.classList.toggle('is-active', n === i);
        if (n === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
      dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
      if (counter) counter.textContent = i + 1;
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    }
    function go(i, instant) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      // read the slides live: choosing a style (caps) reorders them, and a stale list scrolled back to the other style's photo
      var live = track.querySelectorAll('[data-gallery-slide]');
      track.scrollTo({ left: live[i].offsetLeft - track.offsetLeft, behavior: instant ? 'auto' : 'smooth' });
      paint(i);
    }
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        var w = track.clientWidth || 1;
        var i = Math.round(track.scrollLeft / w);
        if (i !== index) paint(Math.max(0, Math.min(slides.length - 1, i)));
      });
    }, { passive: true });
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { go(parseInt(t.getAttribute('data-gallery-thumb'), 10)); });
    });
    if (prev) prev.addEventListener('click', function () { go(index - 1); });
    if (next) next.addEventListener('click', function () { go(index + 1); });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
    });
    window.addEventListener('resize', function () { if (track.scrollWidth > track.clientWidth + 2) go(index, true); });
    track.addEventListener('gallery:reset', function () { paint(0); });
    paint(0);
  }

  /* ------------------------------------------------------------------ */
  /* Buy box                                                             */
  /* ------------------------------------------------------------------ */
  function initProduct(root) {
    var form = root.querySelector('[data-product-form]') || root.querySelector('form[action*="/cart/add"]');
    var dataEl = root.querySelector('[data-product-variants]');
    if (!form || !dataEl) return;
    var variants = [];
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { variants = []; }
    var idInput = form.querySelector('[data-variant-input]');
    var cta = form.querySelector('[data-cta]');
    var stickyCta = root.querySelector('[data-sticky-cta]');
    var errorEl = root.querySelector('[data-product-error]');
    var title = root.getAttribute('data-product-title') || '';
    var template = cta ? cta.getAttribute('data-label-template') : '__PRICE__';
    var soldOut = cta ? cta.getAttribute('data-label-sold-out') : '';

    function variant() {
      var id = Number(idInput.value);
      for (var i = 0; i < variants.length; i++) if (variants[i].id === id) return variants[i];
      return variants[0];
    }
    function addons() {
      return Array.prototype.slice.call(root.querySelectorAll('[data-addon]:checked'));
    }
    var bundle = root.querySelector('[data-bundle]');
    // Shopify automatic discount "Buy X get Y": a second watch is [percent]% off (the cheaper of the two).
    var pairPercent = bundle ? Number(bundle.getAttribute('data-percent') || 0) : 0;
    var qtyInput = form.querySelector('[data-quantity-input]');
    var pairToggle = root.querySelector('[data-bundle-toggle]');
    var pairRow = root.querySelector('[data-pair-row]');
    function pairOn() { return !!(pairToggle && pairToggle.checked); }
    // the chosen second model: null = this same model (quantity 2), else {id, price, title}
    function second() {
      var r = root.querySelector('[data-pair-model]:checked');
      if (!r || r.value === 'same') return null;
      return { id: Number(r.value), price: Number(r.getAttribute('data-price') || 0), title: r.getAttribute('data-title') || '' };
    }
    function quantity() { return pairOn() && !second() ? 2 : 1; }
    function pairOff(a, b) { return Math.round(Math.min(a, b) * pairPercent / 100); }
    // what the second watch adds to the order: its price minus the discount on the cheaper watch
    function pairPlus(v) {
      if (!v) return 0;
      var s = second(); var p2 = s ? s.price : v.price;
      return p2 - pairOff(v.price, p2);
    }
    function watchesPrice(v) { if (!v) return 0; return v.price + (pairOn() ? pairPlus(v) : 0); }
    // an add-on is bought once, or for both watches when that choice is ticked while the pair row is on
    function addonQty(a) {
      var row = a.closest('.addons__row');
      var both = row ? row.querySelector('[data-addon-both]') : null;
      return pairOn() && both && both.checked ? 2 : 1;
    }
    function total() {
      var v = variant();
      return addons().reduce(function (sum, a) { return sum + Number(a.getAttribute('data-addon-price') || 0) * addonQty(a); }, watchesPrice(v));
    }
    function render() {
      var v = variant();
      var t = money(total());
      var label = v && v.available ? template.replace('__PRICE__', t) : soldOut;
      root.querySelectorAll('[data-cta-label]').forEach(function (el) { el.textContent = label; });
      root.querySelectorAll('[data-sticky-total]').forEach(function (el) { el.textContent = t; });
      root.querySelectorAll('[data-product-price]').forEach(function (el) { el.textContent = v ? money(v.price) : ''; });
      root.querySelectorAll('[data-variant-title]').forEach(function (el) { el.textContent = v ? v.title : ''; });
      [cta, stickyCta].forEach(function (b) { if (b) b.disabled = !(v && v.available); });
      // the steel bracelet is always the first variant; any other variant is "another strap"
      var other = !!v && v.steel === false;
      root.querySelectorAll('[data-strap-note]').forEach(function (el) {
        el.hidden = !other;
        if (other) el.textContent = (el.getAttribute('data-template') || '').replace('__STRAP__', (v.strap || v.title.toLowerCase()));
      });
      // limited stock: pieces left of the selected strap ("3 left on black leather"; "Last pieces" at 3 or fewer)
      root.querySelectorAll('[data-strap-stock]').forEach(function (el) {
        if (!v || !v.limited) { el.hidden = true; return; }
        var n = Math.max(0, v.inventory || 0); var strap = v.strap || v.title.toLowerCase();
        el.hidden = v.steel === true && n > 3;
        el.classList.toggle('is-last', n <= 3);
        if (n === 0) { el.textContent = (el.getAttribute('data-tpl-out') || '').replace('__STRAP__', strap); return; }
        var txt = (el.getAttribute('data-tpl-left') || '').replace('__N__', n).replace('__STRAP__', strap);
        el.innerHTML = '';
        if (n <= 3) { var b = document.createElement('strong'); b.textContent = el.getAttribute('data-tpl-last') || ''; el.appendChild(b); el.appendChild(document.createTextNode(' · ')); }
        el.appendChild(document.createTextNode(txt));
      });
      // a pair of the same model needs two of this strap in stock; otherwise the visitor picks another model
      var sameOk = !(v && v.limited && (v.inventory || 0) < 2);
      var sameRadio = root.querySelector('[data-pair-model][value="same"]');
      if (sameRadio) {
        sameRadio.disabled = !sameOk;
        if (!sameOk && sameRadio.checked && pairToggle && pairToggle.checked) {
          pairToggle.checked = false;
          var pk = root.querySelector('[data-pair-pick]'); if (pk) pk.open = true;
          return render();
        }
      }
      // add-ons that only make sense with the steel bracelet (extra strap, sizing tool)
      root.querySelectorAll('[data-steel-only]').forEach(function (row) {
        row.hidden = other;
        if (other) row.querySelectorAll('input:checked').forEach(function (i) { i.checked = false; });
      });
      root.querySelectorAll('.addons__more').forEach(function (more) {
        var any = false;
        more.querySelectorAll('.addons__row').forEach(function (row) { if (!row.hidden) any = true; });
        more.hidden = !any;
      });
      // "for both watches" appears only on rows that are on while the second-watch row is on
      root.querySelectorAll('[data-addon-both-wrap]').forEach(function (wrap) {
        var row = wrap.closest('.addons__row');
        var on = pairOn() && row && row.querySelector('[data-addon]:checked');
        wrap.hidden = !on;
        if (!on) { var b = wrap.querySelector('[data-addon-both]'); if (b) b.checked = false; }
      });
      // V3 gift row: the verse card preview shows while the gift box is switched on
      root.querySelectorAll('[data-gift-row]').forEach(function (row) {
        var preview = row.querySelector('[data-gift-preview]');
        if (preview) preview.hidden = !row.querySelector('[data-addon]:checked');
      });
      root.querySelectorAll('[data-bundle-plus]').forEach(function (el) { el.textContent = v ? money(pairPlus(v)) : ''; });
      // second-watch row: name of the chosen model and its photo
      if (pairRow) {
        var s2 = second(); var picked = root.querySelector('[data-pair-model]:checked');
        var name = pairRow.querySelector('[data-pair-name]'); var same = pairRow.querySelector('[data-pair-same]');
        if (name && picked) name.textContent = picked.getAttribute('data-title') || '';
        if (same) same.hidden = !!s2;
        var thumb = pairRow.querySelector('[data-pair-thumb] img');
        if (thumb && picked && picked.getAttribute('data-img')) { thumb.removeAttribute('srcset'); thumb.src = picked.getAttribute('data-img'); }
      }
      if (qtyInput) qtyInput.value = quantity();
    }

    // option pills -> variant
    form.addEventListener('change', function (e) {
      var input = e.target;
      if (input.matches('[data-option-position]')) {
        var chosen = [];
        form.querySelectorAll('[data-option-position]:checked').forEach(function (el) {
          chosen[parseInt(el.getAttribute('data-option-position'), 10) - 1] = el.value;
        });
        var match = variants.filter(function (v) {
          return chosen.every(function (val, i) { return val === undefined || v.options[i] === val; });
        })[0];
        if (match) {
          idInput.value = match.id;
          // picture options (caps: Plana / Trucker): the photo of the chosen style goes first in the gallery
          if (root.querySelector('.pdp__option--pictures')) showStyleFirst(match.title);
          var out = root.querySelector('[data-option-value="' + input.getAttribute('data-option-position') + '"]');
          if (out) out.textContent = input.value;
          try {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', match.id);
            window.history.replaceState({}, '', url.toString());
          } catch (err) { /* ignore */ }
        }
      }
      if (input.matches('[data-pair-model]')) {
        // choosing a second model switches the second-watch row on and closes the picker
        if (pairToggle) pairToggle.checked = true;
        var pick = input.closest('[data-pair-pick]'); if (pick) pick.open = false;
      }
      if (input.matches('[data-addon], [data-addon-both], [data-option-position], [data-bundle-toggle], [data-pair-model]')) render();
    });

    // Caps whose first photo is the Trucker open with Trucker selected, so the photo and the choice agree.
    (function () {
      if (!root.querySelector('.pdp__option--pictures')) return;
      try { if (new URL(window.location.href).searchParams.get('variant')) return; } catch (err) { return; }
      var first = root.querySelector('[data-gallery-slide] img');
      var alt = first ? (first.getAttribute('alt') || '').toLowerCase() : '';
      if (alt.indexOf('trucker') === -1) return;
      form.querySelectorAll('[data-option-position]').forEach(function (el) {
        if (!el.checked && String(el.value).toLowerCase() === 'trucker') { el.checked = true; el.dispatchEvent(new Event('change', { bubbles: true })); }
      });
    })();

    function showStyleFirst(value) {
      var track = root.querySelector('[data-gallery-track]'); if (!track || !value) return;
      var want = String(value).toLowerCase();
      var slides = Array.prototype.slice.call(track.querySelectorAll('[data-gallery-slide]'));
      var hit = slides.filter(function (sl) { var im = sl.querySelector('img'); return im && (im.getAttribute('alt') || '').toLowerCase().indexOf(want) !== -1; })[0];
      if (!hit) return;
      if (track.firstElementChild !== hit) track.insertBefore(hit, track.firstElementChild);
      track.scrollTo({ left: 0, behavior: 'auto' });
      track.dispatchEvent(new CustomEvent('gallery:reset'));
      var gal = root.querySelector('[data-gallery]'); if (gal && window.innerWidth >= 990) { var top = gal.getBoundingClientRect().top; if (top < 0) window.scrollBy({ top: top - 80, behavior: 'smooth' }); }
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }

    var busy = false;
    function addToCart(button) {
      if (busy || !window.Cart) return;
      var v = variant();
      if (!v || !v.available) return;
      busy = true;
      showError('');
      var watch = { id: v.id, quantity: quantity() };
      var items = [watch];
      var s2 = pairOn() ? second() : null;
      if (s2) {
        items.push({ id: s2.id, quantity: 1 });
      }
      var chosen = addons();
      chosen.forEach(function (a) {
        var id = Number(a.getAttribute('data-addon-id'));
        var n = addonQty(a);
        // mixed pair: an add-on "for both watches" is one line under each watch
        if (s2 && n === 2) {
          items.push({ id: id, quantity: 1, properties: { _for: title } });
          items.push({ id: id, quantity: 1, properties: { _for: s2.title } });
        } else {
          items.push({ id: id, quantity: n, properties: { _for: title } });
        }
      });
      [cta, stickyCta].forEach(function (b) { if (b) b.setAttribute('aria-busy', 'true'); });
      if (button) button.classList.add('is-loading');
      window.Cart.add(items).then(function () {
        chosen.forEach(function (a) { a.checked = false; });
        if (pairToggle) pairToggle.checked = false;
        var same = root.querySelector('[data-pair-model][value="same"]'); if (same) same.checked = true;
        render();
      }).catch(function (err) {
        showError((err && err.message) || (window.themeStrings && window.themeStrings.cartError) || '');
        if (errorEl && errorEl.scrollIntoView && button === stickyCta) errorEl.scrollIntoView({ block: 'center' });
      }).then(function () {
        busy = false;
        [cta, stickyCta].forEach(function (b) { if (b) { b.removeAttribute('aria-busy'); b.classList.remove('is-loading'); } });
      });
    }

    form.addEventListener('submit', function (e) {
      if (!window.Cart) return; // no helper: let the form post normally
      e.preventDefault();
      addToCart(cta);
    });
    if (stickyCta) stickyCta.addEventListener('click', function () { addToCart(stickyCta); });

    // "Choose another model" (inside the second-watch row) opens the picker under the row
    root.querySelectorAll('[data-pair-pick-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var pk = root.querySelector('[data-pair-pick]'); if (!pk) return;
        pk.open = !pk.open; btn.setAttribute('aria-expanded', pk.open ? 'true' : 'false');
      });
    });

    // "Ideal for" chips: bring the gift switch into view (opening "more options" if it sits there)
    root.querySelectorAll('a[href="#pdp-gift"]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        var row = root.querySelector('[data-gift-row]');
        if (!row) return;
        e.preventDefault();
        var more = row.closest('details');
        if (more) more.open = true;
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var sw = row.querySelector('[data-addon]');
        if (sw) { try { sw.focus({ preventScroll: true }); } catch (err) { sw.focus(); } }
      });
    });

    // mark the add-ons that are already in the cart for this watch
    function markInCart(cart) {
      var have = {};
      ((cart && cart.items) || []).forEach(function (i) { if (i.properties && i.properties._for === title) have[i.variant_id || i.id] = true; });
      root.querySelectorAll('[data-addon]').forEach(function (a) {
        var row = a.closest('.addons__row');
        var mark = row ? row.querySelector('[data-addon-incart]') : null;
        if (mark) mark.hidden = !have[Number(a.getAttribute('data-addon-id'))];
      });
    }
    document.addEventListener('cart:updated', function (e) { markInCart(e.detail && e.detail.cart); });
    if (window.Cart) window.Cart.get().then(markInCart).catch(function () {});

    render();
    initSticky(root, cta);
  }

  /* ------------------------------------------------------------------ */
  /* Sticky bar                                                          */
  /* ------------------------------------------------------------------ */
  function initSticky(root, cta) {
    var bar = root.querySelector('[data-sticky-buy]');
    if (!bar || !cta || !('IntersectionObserver' in window)) return;
    var past = false;      // main button has scrolled out above the viewport
    var onScreen = true;   // main button is in the viewport
    var atFooter = false;  // footer is on screen
    var btn = bar.querySelector('[data-sticky-cta]');
    var small = window.matchMedia('(max-width: 749px)');
    function apply() {
      // the bar is there whenever the main button is not on screen (from the first screen on
      // phones, and on desktops where the button starts below the fold)
      // phones: only once the main button has scrolled past (the first screen keeps title and price clear);
      // larger screens: whenever the button is off screen
      var show = (small.matches ? past : !onScreen) && !atFooter;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (btn) btn.tabIndex = show ? 0 : -1;
      document.documentElement.classList.toggle('has-sticky-buy', show);
    }
    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        onScreen = entry.isIntersecting;
        past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      });
      apply();
    }, { threshold: 0 }).observe(cta);
    if (small.addEventListener) small.addEventListener('change', apply);
    var footer = document.querySelector('footer, .shopify-section-group-footer-group');
    if (footer) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { atFooter = entry.isIntersecting; });
        apply();
      }, { threshold: 0 }).observe(footer);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Desktop columns: the image grid and the buy box are both sticky.    */
  /* A column taller than the window sticks by its bottom edge instead   */
  /* of its top, so neither column leaves an empty gap beside the other. */
  /* ------------------------------------------------------------------ */
  function initStickyCols() {
    var cols = document.querySelectorAll('[data-sticky-col]');
    if (!cols.length) return;
    function measure() {
      cols.forEach(function (col) {
        var base = parseFloat(getComputedStyle(col).scrollMarginTop) || 96;
        var room = window.innerHeight - col.offsetHeight - 24;
        col.style.setProperty('--col-top', Math.min(base, room) + 'px');
      });
    }
    measure();
    window.addEventListener('resize', measure);
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(measure);
      cols.forEach(function (col) { ro.observe(col); });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Carousel (related watches): scroll-snap row + two arrows            */
  /* ------------------------------------------------------------------ */
  function initCarousel(root) {
    var track = root.querySelector('[data-carousel-track]');
    var prev = root.querySelector('[data-carousel-prev]');
    var next = root.querySelector('[data-carousel-next]');
    if (!track || !prev || !next) return;
    function step(dir) {
      var item = track.firstElementChild;
      var w = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth;
      var n = Math.max(1, Math.floor(track.clientWidth / w));
      track.scrollBy({ left: dir * w * n, behavior: 'smooth' });
    }
    function paint() {
      var max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
      root.classList.toggle('is-static', max <= 2);
    }
    prev.addEventListener('click', function () { step(-1); });
    next.addEventListener('click', function () { step(1); });
    track.addEventListener('scroll', function () { requestAnimationFrame(paint); }, { passive: true });
    window.addEventListener('resize', paint);
    paint();
  }

  /* model switcher: fade the right edge while more cards follow */
  function initSwitcherFade() {
    document.querySelectorAll('.model-switcher__list').forEach(function (list) {
      function paint() { list.classList.toggle('has-more', list.scrollWidth - list.clientWidth - list.scrollLeft > 4); }
      list.addEventListener('scroll', function () { requestAnimationFrame(paint); }, { passive: true });
      window.addEventListener('resize', paint); paint();
    });
  }

  function init() {
    initSwitcherFade();
    document.querySelectorAll('[data-gallery]').forEach(initGallery);
    document.querySelectorAll('[data-product]').forEach(initProduct);
    document.querySelectorAll('[data-carousel]').forEach(initCarousel);
    initStickyCols();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
